import Decimal from 'decimal.js';

import { Entity } from '../entity';
import { ActivityEntityName, ActivityTagName } from '../shared';
import { Bill, InvoiceStatus, InvoiceType } from './invoicing';
import { PaymentType, PaymentEditorInfo } from '../payment';
import { IFinancialCalculation, InvoiceCalculationModel } from './invoice.calculation.domain';

export class BillCalculation implements IFinancialCalculation {

    private readonly bill: InvoiceCalculationModel;

    constructor(invoice: InvoiceCalculationModel) {

        let { prepaymentTotal, total, amountPaid, amountRefunded, creditAmount, roundingAmount, outstandingAmount, isReconcile } = invoice;

        this.bill = invoice;

        this.bill.amountPaid = amountPaid || 0;
        this.bill.amountRefunded = amountRefunded || 0;
        this.bill.roundingAmount = isReconcile ? (roundingAmount || 0) : 0;
        this.bill.creditAmount = creditAmount || 0;
        this.bill.total = total || 0;
        this.bill.outstandingAmount = outstandingAmount || 0;
        this.bill.prepaymentTotal = prepaymentTotal || 0;

    }

    public getInstance(): InvoiceCalculationModel {
        return this.bill;
    }

    public get<K extends keyof InvoiceCalculationModel>(prop: K): InvoiceCalculationModel[K] {
        return this.bill[prop];
    }

    public set<K extends keyof InvoiceCalculationModel>(
        prop: K,
        value: InvoiceCalculationModel[K]
    ): void {
        this.bill[prop] = value;
    }

    public isFullyPaid(): boolean {

        const { type, prepaymentTotal, amountPaid } = this.bill;

        switch (type) {
            case InvoiceType.CreditNoteSupplier:
                return true;
            case InvoiceType.Bill:
                return this.calculateOutstandingAmount() === 0;
            case InvoiceType.PrepaymentSupplier:
                return new Decimal(prepaymentTotal - amountPaid).equals(0);
            default:
                return false;
        }

    }

    public isApproved(): boolean {
        return this.bill.status === InvoiceStatus.Approved;
    }

    public isType(type: InvoiceType) {
        return this.bill.type === type;
    }


    public getActivityEntityName(): ActivityEntityName {
        switch (this.bill.type) {
            case InvoiceType.CreditNoteSupplier:
                return ActivityEntityName.CreditNoteSupplier;
            case InvoiceType.PrepaymentSupplier:
                return ActivityEntityName.PrepaymentSupplier;
            default:
                return ActivityEntityName.Bill;
        }
    }

    public getActivityTagName(): ActivityTagName {
        switch (this.bill.type) {
            case InvoiceType.PrepaymentSupplier:
                return ActivityTagName.PrepaymentInvoiceId;
            case InvoiceType.CreditNoteSupplier:
            default:
                return ActivityTagName.InvoiceId;
        }
    }

    /** return updated invoice */
    public allocate(allocateAmount: number, currentUser?: Entity): Partial<Bill> | null {

        allocateAmount = allocateAmount || 0;

        if (allocateAmount === 0) return null;

        const amount = this.calculateAmounts({ addCreditAmount: allocateAmount });

        const updatedInvoice: Partial<Bill> = {
            _id: this.bill._id,
            creditAmount: amount.creditAmount,
            outstandingAmount: amount.outstandingAmount,
            updatedBy: currentUser,
        }

        return updatedInvoice;
    }

    public canAllocate(): boolean {
        const { type } = this.bill;

        if (!this.isApproved()) return false;

        switch (type) {
            case InvoiceType.CreditNoteSupplier:
            case InvoiceType.PrepaymentSupplier:
                return this.getRemainingCredit() > 0;
            case InvoiceType.Bill:
                return this.get('outstandingAmount') > 0;
            default:
                return false;
        }
    }

    public canMakePayment(): boolean {
        return this.getPaymentInfo() !== null;
    }

    public getPaymentInfo(): PaymentEditorInfo | null {

        if (!this.isApproved()) return null;

        const remainingCredit = this.calculateRemainingCredit();
        const refundableCreditNote = this.isType(InvoiceType.CreditNoteSupplier) && remainingCredit > 0;
        const refundablePrepayment = this.isType(InvoiceType.PrepaymentSupplier) && remainingCredit > 0;

        if (refundableCreditNote) {
            return { amount: remainingCredit, amountEditable: true, title: 'Refund', type: PaymentType.PayableCredit };
        } else if (refundablePrepayment) {
            return { amount: remainingCredit, amountEditable: true, title: 'Refund', type: PaymentType.PayablePrepayment };
        }

        const outstandingAmount = this.calculateOutstandingAmount();
        const hasOutstandingAmountPrepayment = this.isType(InvoiceType.PrepaymentSupplier) && outstandingAmount > 0;
        const hasOutstandingAmountInvoice = this.isType(InvoiceType.Bill) && outstandingAmount > 0;

        if (hasOutstandingAmountPrepayment || hasOutstandingAmountInvoice) {
            return { amount: outstandingAmount, amountEditable: hasOutstandingAmountInvoice, title: 'Pay', type: PaymentType.Payable };
        }

        return null;
    }


    /**
     * This is the outstanding amount calculation. Not the remaining credit calculation 
     * @returns calculated outstanding amount
     */
    public calculateOutstandingAmount(): number {

        const { prepaymentTotal, total, amountPaid, creditAmount, roundingAmount, type } = this.bill;

        switch (type) {
            case InvoiceType.CreditNoteSupplier:
                return 0; // no payment for credit note
            case InvoiceType.PrepaymentSupplier:
                return new Decimal(prepaymentTotal - amountPaid).toDP(2).toNumber() // only if pay partially, but for now you only can pay 1 full payment
            default:
                // Invoice
                return new Decimal(total - (amountPaid + creditAmount + roundingAmount)).toDP(2).toNumber();
        }
    }

    public getRemainingCredit(): number {
        const { outstandingAmount, type } = this.bill;
        switch (type) {
            case InvoiceType.CreditNoteSupplier:
            case InvoiceType.PrepaymentSupplier:
                return Math.abs(outstandingAmount);
            default:
                return 0;
        }
    }

    public calculateRemainingCredit(): number {
        const { total, amountPaid, amountRefunded, prepaymentTotal, creditAmount, type, outstandingAmount } = this.bill;
        switch (type) {
            case InvoiceType.CreditNoteSupplier: {
                return Math.abs(total) - amountRefunded - creditAmount
            }
            case InvoiceType.PrepaymentSupplier: {

                const isNew = amountPaid === 0 && outstandingAmount === 0;
                if (isNew || !this.isFullyPaid()) {
                    return 0;
                }

                // recalculate the remaining credit
                const remaining = prepaymentTotal - (amountRefunded + creditAmount)
                return Math.abs(remaining);
            }
            default:
                return 0;
        }
    }

    /**
     * get the outstanding amount from the invoice for payment
     */
    public getPaymentAmount(
        config: { absolute: boolean } = { absolute: true }
    ): number {

        const { type, prepaymentTotal, outstandingAmount, amountPaid } = this.bill;

        let value = 0

        switch (type) {
            case InvoiceType.CreditNoteSupplier:
                // no payment for credit note
                break;
            case InvoiceType.PrepaymentSupplier:
                value = prepaymentTotal - amountPaid;
                break;
            default:
                value = outstandingAmount;
                break;
        }
        return config?.absolute ? Math.abs(value) : value;
    }


    /**
     * Recalculate all amount in every type of invoice (PP, CR, Invoice)
     * @param request any added amount to the invoice. And if reconcile stage is changed recalculated the rounding amount
     * @returns all amount infos
     */
    public calculateAmounts(
        request?: { addPaidAmount?: number, addCreditAmount?: number, addRefundedAmount?: number, newReconcileStage?: boolean }
    ): Pick<InvoiceCalculationModel, 'amountPaid' | 'amountRefunded' | 'creditAmount' | 'outstandingAmount' | 'roundingAmount'> {

        let { total, prepaymentTotal, amountPaid, creditAmount, amountRefunded, roundingAmount, type } = this.bill;

        const newPaidAmount = amountPaid + (request?.addPaidAmount || 0);
        const newCreditAmount = creditAmount + (request?.addCreditAmount || 0);
        const newRefundedAmount = amountRefunded + (request?.addRefundedAmount || 0);
        let newRoundingAmount = roundingAmount;

        let outstandingAmount = 0;

        switch (type) {
            case InvoiceType.Bill:
                const hasNewReconcileStage = typeof request?.newReconcileStage === 'boolean';


                if (!hasNewReconcileStage) {
                    outstandingAmount = total - (newPaidAmount + newCreditAmount + newRoundingAmount);
                    break;
                }

                const diffAmount = total - (newPaidAmount + newCreditAmount);

                if (request?.newReconcileStage) {
                    newRoundingAmount = diffAmount;
                    outstandingAmount = 0;
                } else {
                    newRoundingAmount = 0;
                    outstandingAmount = diffAmount;
                }

                break;
            case InvoiceType.CreditNoteSupplier:
                // CR total is negative
                outstandingAmount = total + (newCreditAmount + newRefundedAmount);
                break;
            case InvoiceType.PrepaymentSupplier:
                // PP total is negative
                const isNew = newPaidAmount === 0;

                if (isNew || !this.isFullyPaid()) {
                    // hacky way to make sure the prepayment invoice is not paid yet
                    outstandingAmount = 0;
                    break;
                }

                outstandingAmount = -(prepaymentTotal - (newCreditAmount + newRefundedAmount));
                break;
        }

        return {
            amountPaid: new Decimal(newPaidAmount ?? 0).toDP(2).toNumber(),
            creditAmount: new Decimal(newCreditAmount ?? 0).toDP(2).toNumber(),
            amountRefunded: new Decimal(newRefundedAmount ?? 0).toDP(2).toNumber(),
            roundingAmount: new Decimal(newRoundingAmount ?? 0).toDP(2).toNumber(),
            outstandingAmount: new Decimal(outstandingAmount ?? 0).toDP(2).toNumber(),
        };
    }

}